import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Logo } from '../components/Logo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ExerciseDetailDialog } from '../components/ExerciseDetailDialog';
import { ExercisePicker } from '../components/ExercisePicker';
import { PlanExerciseCard, SupersetLink } from '../components/PlanExerciseCard';
import { GeneratorDraftDialog, GeneratorDraftInfo } from '../components/GeneratorDraft';
import { ArrowLeft, Plus, Save, Wand2, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

import { localISODate } from '../lib/utils';
import { DaySelector } from '../components/DaySelector';
import {
  BLOCK_ROLLING, BLOCK_STRETCHING, dayItems, hasBlock, isLinked, moveItem, newPlanItem, removeItem,
  rollingItems, setBlock, stretchingItems, swapExercise, toggleSuperset, withDay,
} from '../lib/planEditing';
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export const PlanBuilderPage = () => {
  const { id, planId } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [picker, setPicker] = useState(null); // { replacing: index in the day | null }
  const [selectedDay, setSelectedDay] = useState(1);
  const [previewExercise, setPreviewExercise] = useState(null);
  const [planMeta, setPlanMeta] = useState(null); // {status, source} of the plan being edited
  const [generatorOpen, setGeneratorOpen] = useState(false);
  
  const [planData, setPlanData] = useState({
    plan_name: '',
    frequency: 3,
    start_date: localISODate(),
    end_date: '',
    notes: '',
    exercises: [] // [{exercise_id, exercise_name, day_number, order_index, sets, reps, tempo, rir, rpe, rest_seconds, weight_kg, notes}]
  });

  useEffect(() => {
    fetchData();
  }, [id, planId]);

  const fetchData = async () => {
    try {
      const requests = [
        axios.get(`${API_URL}/api/clients/${id}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/exercises`, { withCredentials: true })
      ];
      if (planId) {
        requests.push(axios.get(`${API_URL}/api/training-plans/${planId}`, { withCredentials: true }));
      }
      const [clientRes, exercisesRes, planRes] = await Promise.all(requests);
      setClient(clientRes.data);
      setExercises(exercisesRes.data);
      if (planRes) {
        setPlanData({
          plan_name: planRes.data.plan_name,
          frequency: planRes.data.frequency,
          start_date: planRes.data.start_date || localISODate(),
          end_date: planRes.data.end_date || '',
          notes: planRes.data.notes || '',
          exercises: planRes.data.exercises
        });
        setPlanMeta({ status: planRes.data.status, source: planRes.data.source });
        setSelectedDay(1);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const items = dayItems(planData.exercises, selectedDay);
  const setItems = (next) => setPlanData(p => ({ ...p, exercises: withDay(p.exercises, selectedDay, next) }));

  const updateExercise = (index, field, value) => setItems(items.map((e, i) => (i === index ? { ...e, [field]: value } : e)));

  const pickExercise = (exercise) => {
    if (picker.replacing === null) setItems([...items, newPlanItem(exercise, selectedDay)]);
    else setItems(items.map((e, i) => (i === picker.replacing ? swapExercise(e, exercise) : e)));
  };

  const addRolling = () => {
    const added = rollingItems(exercises, selectedDay);
    if (added.length === 0) toast.error('Brak ćwiczeń rolowania w bibliotece');
    else setItems([...added, ...items]);
  };

  const addStretching = () => {
    const added = stretchingItems(exercises, items, selectedDay);
    if (added.length === 0) toast.error('Brak ćwiczeń rozciągających w bibliotece');
    else setItems([...items, ...added]);
  };

  const handleSave = async (activate = false) => {
    if (!planData.plan_name || planData.exercises.length === 0) {
      toast.error('Podaj nazwę planu i dodaj co najmniej jedno ćwiczenie');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        plan_name: planData.plan_name,
        frequency: planData.frequency,
        start_date: planData.start_date || null,
        end_date: planData.end_date || null,
        notes: planData.notes,
        exercises: planData.exercises,
        ...(activate === true ? { status: 'active' } : {})
      };
      if (planId) {
        await axios.put(`${API_URL}/api/training-plans/${planId}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/training-plans`, { ...payload, client_id: id }, { withCredentials: true });
      }
      navigate(`/client/${id}`);
    } catch (error) {
      console.error('Error saving plan:', error);
      toast.error('Błąd zapisu planu');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]">
        <div className="text-white">Ładowanie...</div>
      </div>
    );
  }

  const isGeneratorDraft = planMeta?.source === 'generator' && planMeta?.status === 'draft';
  // Generator plans repeat days within the week (A-B-A), so they have fewer distinct days than trainings per week.
  const dayCount = planMeta?.source === 'generator' && planData.exercises.length
    ? Math.max(...planData.exercises.map(e => e.day_number)) : planData.frequency;

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="plan-builder-page">
      <nav className="border-b border-[#2A2A2A] px-4 sm:px-8 py-4 sm:py-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-2">
          <Logo size="md" />
          <Button
            onClick={() => navigate(`/client/${id}`)}
            variant="ghost"
            className="text-[#A1A1A1] hover:text-white px-2 sm:px-4 shrink-0"
          >
            <ArrowLeft className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Powrót</span>
          </Button>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-8 sm:py-12">
        <div className="mb-12">
          <h1 className="display-text text-5xl text-white mb-2">{planId ? 'Edytuj Plan' : 'Generator Planu'}</h1>
          <p className="body-text text-[#A1A1A1] text-lg">{client?.name}</p>
          {!planId && client?.access_type !== 'self_service' && (
            <Button onClick={() => setGeneratorOpen(true)} data-testid="open-generator"
              className="mt-6 bg-[#1E1E1E] hover:bg-[#2A2A2A] text-[#C9A84C] border border-[#C9A84C] rounded-full">
              <Wand2 className="w-4 h-4 mr-2" /> Szkic z generatora
            </Button>
          )}
        </div>

        {planId && planMeta?.source === 'generator' && <GeneratorDraftInfo planId={planId} />}

        {/* Plan Info */}
        <div className="card-surface rounded-3xl p-8 mb-8">
          <h2 className="heading-text text-2xl text-white mb-6">Informacje o planie</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <Label className="label-text text-[#A1A1A1] mb-2 block">NAZWA PLANU</Label>
              <Input
                value={planData.plan_name}
                onChange={(e) => setPlanData({ ...planData, plan_name: e.target.value })}
                placeholder="np. Siła A"
                className="bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl text-white"
              />
            </div>
            <div>
              <Label className="label-text text-[#A1A1A1] mb-2 block">CZĘSTOTLIWOŚĆ (DNI/TYG)</Label>
              <Input
                type="number"
                min="1"
                max="7"
                value={planData.frequency}
                onChange={(e) => setPlanData({ ...planData, frequency: parseInt(e.target.value) })}
                className="bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl text-white"
              />
            </div>
            <div>
              <Label className="label-text text-[#A1A1A1] mb-2 block">DATA ROZPOCZĘCIA</Label>
              <Input
                type="date"
                value={planData.start_date}
                onChange={(e) => setPlanData({ ...planData, start_date: e.target.value })}
                className="bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl text-white"
              />
            </div>
          </div>
        </div>

        {/* Day Selector */}
        <DaySelector
          selected={selectedDay}
          onSelect={setSelectedDay}
          days={[...Array(dayCount || 0)].map((_, i) => {
            const dayExercises = planData.exercises.filter(e => e.day_number === i + 1);
            return { day: i + 1, label: dayExercises.find(e => e.day_label)?.day_label, count: dayExercises.length };
          })}
        />

        {/* Exercises for Selected Day */}
        <div className="mb-8">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <h2 className="heading-text text-2xl text-white">Trening {selectedDay}</h2>
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={addRolling}
                disabled={hasBlock(items, BLOCK_ROLLING)}
                variant="ghost"
                className="border border-[#2A2A2A] text-[#A1A1A1] hover:text-white rounded-full"
                title="Plecy, przód uda, bok uda, pośladek. Dobre po długim siedzeniu."
                data-testid="add-rolling"
              >
                <Plus className="w-4 h-4 mr-2" /> Rolowanie
              </Button>
              <Button
                onClick={addStretching}
                disabled={hasBlock(items, BLOCK_STRETCHING)}
                variant="ghost"
                className="border border-[#2A2A2A] text-[#A1A1A1] hover:text-white rounded-full"
                title="Delikatne rozciąganie na koniec treningu"
                data-testid="add-stretching"
              >
                <Plus className="w-4 h-4 mr-2" /> Rozciąganie
              </Button>
              <Button
                onClick={() => setPicker({ replacing: null })}
                className="bg-[#1E1E1E] hover:bg-[#2A2A2A] text-white border border-[#C9A84C] rounded-full"
                data-testid="add-exercise-to-plan"
              >
                <Plus className="w-4 h-4 mr-2" />
                Dodaj ćwiczenie
              </Button>
            </div>
          </div>

          {items.length === 0 ? (
            <div className="card-surface rounded-3xl p-12 text-center">
              <p className="body-text text-[#A1A1A1]">Brak ćwiczeń. Dodaj pierwsze ćwiczenie do tego treningu.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {items.map((ex, index) => (
                <React.Fragment key={index}>
                  <PlanExerciseCard
                    ex={ex}
                    index={index}
                    total={items.length}
                    onChange={(field, value) => updateExercise(index, field, value)}
                    onBlock={(value) => setItems(setBlock(items, index, value))}
                    onMove={(dir) => setItems(moveItem(items, index, dir))}
                    onSwap={() => setPicker({ replacing: index })}
                    onRemove={() => setItems(removeItem(items, index))}
                    onPreview={() => setPreviewExercise(exercises.find(e => e.id === ex.exercise_id))}
                  />
                  {index + 1 < items.length && !ex.circuit_group && !items[index + 1].circuit_group && (
                    <SupersetLink
                      linked={isLinked(items, index)}
                      onToggle={() => setItems(toggleSuperset(items, index))}
                      testId={`superset-link-${index}`}
                    />
                  )}
                </React.Fragment>
              ))}
              {items.some(e => e.superset_group) && (
                <p className="text-sm text-[#A1A1A1] text-center">
                  W super-serii klient robi ćwiczenia jedno po drugim, bez przerwy. Odpoczywa po ostatnim z nich, tyle ile ustawiono przy tym ćwiczeniu.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Save Button */}
        <div className="flex justify-end gap-4">
          <Button
            onClick={() => navigate(`/client/${id}`)}
            variant="ghost"
            className="text-[#A1A1A1]"
          >
            Anuluj
          </Button>
          <Button
            onClick={() => handleSave(false)}
            disabled={saving || !planData.plan_name || planData.exercises.length === 0}
            className="bg-[#1E1E1E] hover:bg-[#2A2A2A] text-white border border-[#C9A84C] rounded-full px-12 py-6 text-lg"
          >
            <Save className="w-5 h-5 mr-2" />
            {saving ? 'Zapisywanie...' : isGeneratorDraft ? 'Zapisz szkic' : 'Zapisz plan'}
          </Button>
          {isGeneratorDraft && (
            <Button
              onClick={() => handleSave(true)}
              disabled={saving || !planData.plan_name || planData.exercises.length === 0}
              className="bg-[#C9A84C] hover:bg-[#B89A3E] text-black rounded-full px-10 py-6 text-lg"
              data-testid="activate-plan"
            >
              <CheckCircle2 className="w-5 h-5 mr-2" /> Aktywuj plan
            </Button>
          )}
        </div>
      </main>

      {client && (
        <GeneratorDraftDialog open={generatorOpen} onOpenChange={setGeneratorOpen} client={client}
          onCreated={(newId) => { setGeneratorOpen(false); navigate(`/client/${id}/plan-builder/${newId}`); }} />
      )}

      <ExercisePicker
        open={!!picker}
        onOpenChange={(open) => !open && setPicker(null)}
        exercises={exercises}
        replacing={picker?.replacing != null ? items[picker.replacing] : null}
        onPick={pickExercise}
        onPreview={setPreviewExercise}
      />

      <ExerciseDetailDialog
        exercise={previewExercise}
        open={!!previewExercise}
        onOpenChange={(open) => !open && setPreviewExercise(null)}
      />
    </div>
  );
};